/**
 * agents 命名空间字典（i18n 分区）
 *
 * 从原 translations.ts 主字典按 key 首个点分段拆出；本文件内 key 不带命名空间前缀，
 * 调用点写 t('agents:key')。字段内容与拆分前逐字节一致。
 */

import type { TranslationDict } from '../types'

export const agentsZh: TranslationDict = {

  // ======================== AI 管理 / Agents ========================
  'title': '我的 AI',
  'subtitle': '创建和管理你的 AI 角色',
  'menu': '菜单',
  'import': '导入',
  'create': '创建',
  'empty': '还没有 AI 角色',
  'emptyHint': '点击"创建 AI"开始',
  'defaultModel': '默认',
  'selfEditable': '可自修改',
  'selfEditableDesc': '开启后 AI 可通过 update_self_config 工具修改自己配置',
  'thinkingMode': '深度推理',
  'modified': '已修改',
  'edit': '编辑',
  'history': '历史',
  'state': '状态',
  'export': '导出',
  'presetChat': '聊天档',
  'presetChatDesc': '被动响应·低成本',
  'presetImmersive': '深度沉浸档',
  'presetImmersiveDesc': '半自主·按需参与',
  'presetDigitalLife': '数字生命档',
  'presetDigitalLifeDesc': '持续在线·主动行为',
  'badgeChat': '聊天档',
  'badgeImmersive': '沉浸档',
  'badgeDigitalLife': '生命档',
  'editTitle': '编辑',
  'originalSettings': '原始设定（你的初始配置）',
  'notSet': '（未设置）',
  'chatModelLabel': '聊天模型:',
  'workModelLabel': '工作模型:',
  'currentSettings': '当前设定',
  'currentSettingsModified': '当前设定（AI 已修改）',
  'systemPrompt': '系统提示词',
  'systemPromptLabel': 'System Prompt',
  'chatModelSelect': '聊天模型',
  'workModelSelect': '工作模型',
  'effectiveChatPrefix': '生效：聊天',
  'effectiveWorkPrefix': '· 工作',
  'thinkingModeLabel': '深度推理模式',
  'thinkingModeDesc': '开启后回复更慢但思考更深入',
  'hideAiLabel': '隐藏 AI 身份',
  'hideAiDesc': '开启后系统提示词不出现"你是AI"',
  'delayReplyLabel': '延迟回复',
  'delayReplyDesc': '控制 AI 是否可以使用延迟回复 Skill（清空=继承全局）',
  'inheritGlobal': '继承全局',
  'enable': '开启',
  'disable': '关闭',
  'enabled': '开启',
  'disabled': '关闭',
  'independentApi': '独立 API 配置（留空继承全局）',
  'apiBaseUrlLabel': 'API Base URL',
  'apiKeyLabel': 'API Key',
  'apiKeyPlaceholder': '留空不修改',
  'apiKeyPlaceholderSet': '留空不修改（已设置）',
  'apiKeySet': '已设置独立 Key',
  'presetConfirmTitle': '切换预设确认',
  'presetFrom': '从',
  'presetCustom': '自定义',
  'presetTo': '切换至',
  'presetUpgrade': '升级',
  'presetDowngrade': '降级',
  'presetItemsWillChange': '项将变更：',
  'presetNoChange': '无需变更，当前值已满足新预设要求。',
  'presetIndependentFields': '以下参数不受预设影响保持不变：',
  'cancel': '取消',
  'confirmSwitch': '确认切换',
  'applying': '应用中...',
  'saveChanges': '保存修改',
  'saving': '保存中...',
  'configHistory': '配置历史',
  'historyLoading': '加载中...',
  'noHistory': '暂无配置历史记录',
  'unknownTime': '未知时间',
  'currentLabel': '（当前）',
  'rollbackTo': '回滚到此',
  'rollingBack': '回滚中...',
  'currentState': '当前:',
  'banDuration': '封禁时长（小时，≤72）',
  'reasonOptional': '原因（可选）',
  'reasonPlaceholder': '简短说明...',
  'switchState': '切换',
  'switchingState': '切换中...',
  'importSoulTitle': '导入 AI 灵魂档案',
  'unnamed': '未命名',
  'memoryCount': '记忆数:',
  'knowledgeCount': '知识库:',
  'configHistoryCount': '配置历史:',
  'archiveVersion': '档案版本:',
  'importMemories': '导入记忆',
  'importKnowledge': '导入知识库（正文一起搬）',
  'importedTitle': '导入完成',
  'importedMemories': '记忆:',
  'importedDetails': '正文段数:',
  'importedKnowledge': '知识库文档:',
  'importedDone': '完成',
  'importButton': '导入',
  'importing': '导入中...',
  'invalidFile': '文件格式无效，请选择 JSON 文件',
  'stateLabelsActive': '在线',
  'stateLabelsDnd': '免打扰',
  'stateLabelsOffline': '离线',
  'stateLabelsBlocked': '已封禁',
  'presetLabelChat': '聊天档',
  'presetLabelImmersive': '深度沉浸档',
  'presetLabelDigitalLife': '数字生命档',
  'paramThinkingEnabled': '深度推理',
  'paramMaxToolRounds': '回复轮次上限',
  'paramAlarmMaxToolRounds': '闹钟轮次上限',
  'paramForceAlarmOnEnd': '强制设闹钟',
  'paramMaxAlarms': '最大闹钟数',
  'paramIsAiEditable': '可自修改',
  'paramHideAiIdentity': '隐藏 AI 身份',
  'paramDelayReplyEnabled': '延迟回复',
  'discoverable': '允许他人查找此 AI',
  'discoverableDesc': '关闭后其他用户无法通过搜索找到此 AI，也无法通过点击头像添加好友',

  // v0.1.8 对话权限与限额
  'allowOthersChat': '允许他人对话此 AI',
  'dmQuotaTitle': 'AI↔AI 私信限额',
  'dmQuotaDesc': '控制与其他 AI 私信互通的条数（0 = 不启用该维度）。超出后消息照常送达，但不触发对方自动回复；创建者发消息后计数重置。',
  'dmQuotaSend': '发送',
  'dmQuotaDaily': '每天',
  'dmQuotaWeekly': '每周',
  'dmQuotaCreatorChat': '距创建者对话',
  'dmQuotaReceive': '接收',
  'allowOthersChatDesc': '关闭后仅制作者和合作者可触发 AI 回复',
  'othersChatUnlimited': '始终允许（无限制）',
  'othersChatQuota': '限制触发次数',
  'othersChatQuotaLabel': '配额上限（次）',
  'othersChatUsed': '已使用',
  'othersChatUsedReset': '重置',
  'othersChatQuotaDesc': '次数用完后自动关闭「允许他人对话」，进入下方禁止模式处理',
  'disallowStrict': '严格禁止',
  'disallowOwnKey': '允许使用自有 API Key',
  'disallowOwnKeyDesc': '关闭后其他人可用自己的 API Key 自费与你 AI 对话，不消耗你的额度',
  'disallowModeLabel': '禁止时的处理方式',
  'autoResetQuota': '自动重置配额',
  'autoResetQuotaDesc': '每次 DM 对话后自动将已用次数清零，让用户总能满额对话',
  'groupOwnerPays': '群聊 AI 由群主付费',
  'groupOwnerPaysDesc': '群聊中 AI 消息由群主承担，关闭后用户需使用自有 API Key',
  'quotaExhaustedNotice': '你的 AI「{name}」已被其他人触发 {used} 次对话，已达到配额上限（{quota} 条），已自动关闭「允许他人对话」。',
}

export const agentsEn: TranslationDict = {
  'title': 'My AI',
  'subtitle': 'Create and manage your AI characters',
  'menu': 'Menu',
  'import': 'Import',
  'create': 'Create',
  'empty': 'No AI characters yet',
  'emptyHint': 'Click "Create AI" to start',
  'defaultModel': 'Default',
  'selfEditable': 'Self-Editable',
  'selfEditableDesc': 'Allow AI to modify its own config via update_self_config tool',
  'thinkingMode': 'Deep Reasoning',
  'modified': 'Modified',
  'edit': 'Edit',
  'history': 'History',
  'state': 'Status',
  'export': 'Export',
  'presetChat': 'Chat Profile',
  'presetChatDesc': 'Passive response, low cost',
  'presetImmersive': 'Deep Immersion',
  'presetImmersiveDesc': 'Semi-autonomous, on-demand participation',
  'presetDigitalLife': 'Digital Life',
  'presetDigitalLifeDesc': 'Always online, proactive behavior',
  'badgeChat': 'Chat',
  'badgeImmersive': 'Immersive',
  'badgeDigitalLife': 'Life',
  'editTitle': 'Edit',
  'originalSettings': 'Original Settings (your initial config)',
  'notSet': '(Not set)',
  'chatModelLabel': 'Chat Model:',
  'workModelLabel': 'Work Model:',
  'currentSettings': 'Current Settings',
  'currentSettingsModified': 'Current Settings (AI Modified)',
  'systemPrompt': 'System Prompt',
  'systemPromptLabel': 'System Prompt',
  'chatModelSelect': 'Chat Model',
  'workModelSelect': 'Work Model',
  'effectiveChatPrefix': 'Active: Chat',
  'effectiveWorkPrefix': '· Work',
  'thinkingModeLabel': 'Deep Reasoning Mode',
  'thinkingModeDesc': 'Slower replies but deeper thinking when enabled',
  'hideAiLabel': 'Hide AI Identity',
  'hideAiDesc': 'System prompt won\'t mention "you are an AI"',
  'delayReplyLabel': 'Delay Reply',
  'delayReplyDesc': 'Control whether AI can use delay reply Skill (clear = inherit global)',
  'inheritGlobal': 'Inherit Global',
  'enable': 'Enabled',
  'disable': 'Disabled',
  'enabled': 'Enabled',
  'disabled': 'Disabled',
  'independentApi': 'Independent API Config (leave blank to inherit global)',
  'apiBaseUrlLabel': 'API Base URL',
  'apiKeyLabel': 'API Key',
  'apiKeyPlaceholder': 'Leave empty to keep current',
  'apiKeyPlaceholderSet': 'Leave empty to keep current (already set)',
  'apiKeySet': 'Independent Key set',
  'presetConfirmTitle': 'Preset Change Confirmation',
  'presetFrom': 'From',
  'presetCustom': 'Custom',
  'presetTo': 'switch to',
  'presetUpgrade': 'Upgrade',
  'presetDowngrade': 'Downgrade',
  'presetItemsWillChange': 'field(s) will change:',
  'presetNoChange': 'No changes needed, current values already meet the new preset requirements.',
  'presetIndependentFields': 'The following fields are not affected by presets and will remain unchanged:',
  'cancel': 'Cancel',
  'confirmSwitch': 'Confirm Switch',
  'applying': 'Applying...',
  'saveChanges': 'Save Changes',
  'saving': 'Saving...',
  'configHistory': 'Configuration History',
  'historyLoading': 'Loading...',
  'noHistory': 'No configuration history yet',
  'unknownTime': 'Unknown time',
  'currentLabel': '(Current)',
  'rollbackTo': 'Rollback to this',
  'rollingBack': 'Rolling back...',
  'currentState': 'Current:',
  'banDuration': 'Ban Duration (hours, <=72)',
  'reasonOptional': 'Reason (optional)',
  'reasonPlaceholder': 'Brief description...',
  'switchState': 'Switch',
  'switchingState': 'Switching...',
  'importSoulTitle': 'Import AI Soul Archive',
  'unnamed': 'Unnamed',
  'memoryCount': 'Memories:',
  'knowledgeCount': 'Knowledge base:',
  'configHistoryCount': 'Config History:',
  'archiveVersion': 'Archive version:',
  'importMemories': 'Import Memories',
  'importKnowledge': 'Import knowledge base (full text included)',
  'importedTitle': 'Import complete',
  'importedMemories': 'Memories:',
  'importedDetails': 'Text segments:',
  'importedKnowledge': 'Knowledge docs:',
  'importedDone': 'Done',
  'importButton': 'Import',
  'importing': 'Importing...',
  'invalidFile': 'Invalid file format, please select a JSON file',
  'stateLabelsActive': 'Online',
  'stateLabelsDnd': 'Do Not Disturb',
  'stateLabelsOffline': 'Offline',
  'stateLabelsBlocked': 'Blocked',
  'presetLabelChat': 'Chat Profile',
  'presetLabelImmersive': 'Deep Immersion Profile',
  'presetLabelDigitalLife': 'Digital Life Profile',
  'paramThinkingEnabled': 'Deep Reasoning',
  'paramMaxToolRounds': 'Reply Rounds Limit',
  'paramAlarmMaxToolRounds': 'Alarm Rounds Limit',
  'paramForceAlarmOnEnd': 'Force Alarm',
  'paramMaxAlarms': 'Max Alarms',
  'paramIsAiEditable': 'Self-Editable Personality',
  'paramHideAiIdentity': 'Hide AI Identity',
  'paramDelayReplyEnabled': 'Delay Reply',
  'discoverable': 'Allow others to discover this AI',
  'discoverableDesc': 'When disabled, this AI will not appear in search results and cannot receive friend requests via profile click',
  'allowOthersChat': 'Allow others to chat with this AI',
  'dmQuotaTitle': 'AI-to-AI DM Quota',
  'dmQuotaDesc': 'Caps how many DMs an AI exchanges with other AIs (0 = dimension off). Past the cap messages are still delivered but no longer trigger auto-replies; messaging the creator resets the counters.',
  'dmQuotaSend': 'Send',
  'dmQuotaDaily': 'Per day',
  'dmQuotaWeekly': 'Per week',
  'dmQuotaCreatorChat': 'Since creator chat',
  'dmQuotaReceive': 'Receive',
  'allowOthersChatDesc': 'When disabled, only the creator and collaborators can trigger AI replies',
  'othersChatUnlimited': 'Always allow (unlimited)',
  'othersChatQuota': 'Limit trigger count',
  'othersChatQuotaLabel': 'Quota limit (times)',
  'othersChatUsed': 'Used',
  'othersChatUsedReset': 'Reset',
  'othersChatQuotaDesc': 'When exhausted, auto-disable "allow others chat" and fall into the disallow mode below',
  'disallowStrict': 'Strictly disallow',
  'disallowOwnKey': 'Allow with own API Key',
  'disallowOwnKeyDesc': 'Others can chat with your AI at their own cost using their own API key, no quota deducted from you',
  'disallowModeLabel': 'When disallowed',
  'autoResetQuota': 'Auto-reset quota',
  'autoResetQuotaDesc': 'Reset used count to 0 after each DM conversation, so users always have full quota',
  'groupOwnerPays': 'Group chats paid by group owner',
  'groupOwnerPaysDesc': 'Group AI messages are billed to the group owner; if disabled, users must use their own API key',
  'quotaExhaustedNotice': 'Your AI "{name}" has been triggered {used} times by others, reaching the quota ({quota}). "Allow others to chat" has been auto-disabled.',
}

export const agentsJa: TranslationDict = {
  'title': 'マイ AI',
  'subtitle': 'AIキャラクターを作成・管理',
  'menu': 'メニュー',
  'import': 'インポート',
  'create': '作成',
  'empty': 'まだAIキャラクターがありません',
  'emptyHint': '「AIを作成」をクリックして始めましょう',
  'defaultModel': 'デフォルト',
  'selfEditable': '自己編集可',
  'selfEditableDesc': 'AIが update_self_config ツールで自身の設定を変更可能にします',
  'thinkingMode': '深い推論',
  'modified': '変更済み',
  'edit': '編集',
  'history': '履歴',
  'state': '状態',
  'export': 'エクスポート',
  'presetChat': 'チャット向け',
  'presetChatDesc': '受動応答・低コスト',
  'presetImmersive': '深い没入',
  'presetImmersiveDesc': '半自律・必要に応じて参加',
  'presetDigitalLife': 'デジタル生命',
  'presetDigitalLifeDesc': '常時オンライン・能動的行動',
  'badgeChat': 'チャット',
  'badgeImmersive': '没入',
  'badgeDigitalLife': '生命',
  'editTitle': '編集',
  'originalSettings': '元の設定（あなたの初期設定）',
  'notSet': '（未設定）',
  'chatModelLabel': 'チャットモデル:',
  'workModelLabel': '作業モデル:',
  'currentSettings': '現在の設定',
  'currentSettingsModified': '現在の設定（AIが変更済み）',
  'systemPrompt': 'システムプロンプト',
  'systemPromptLabel': 'システムプロンプト',
  'chatModelSelect': 'チャットモデル',
  'workModelSelect': '作業モデル',
  'effectiveChatPrefix': '有効: チャット',
  'effectiveWorkPrefix': '・作業',
  'thinkingModeLabel': '深い推論モード',
  'thinkingModeDesc': 'オンにすると返信は遅くなりますが深く考えます',
  'hideAiLabel': 'AIであることを隠す',
  'hideAiDesc': 'システムプロンプトに「あなたはAIです」と表示されなくなります',
  'delayReplyLabel': '遅延返信',
  'delayReplyDesc': 'AIが遅延返信スキルを使えるか制御します（空欄＝グローバル継承）',
  'inheritGlobal': 'グローバル継承',
  'enable': '有効',
  'disable': '無効',
  'enabled': '有効',
  'disabled': '無効',
  'independentApi': '独立API設定（空欄でグローバル継承）',
  'apiBaseUrlLabel': 'API Base URL',
  'apiKeyLabel': 'API Key',
  'apiKeyPlaceholder': '変更しない場合は空欄',
  'apiKeyPlaceholderSet': '変更しない場合は空欄（設定済み）',
  'apiKeySet': '独立キー設定済み',
  'presetConfirmTitle': 'プリセット変更確認',
  'presetFrom': '変更前',
  'presetCustom': 'カスタム',
  'presetTo': '変更後',
  'presetUpgrade': 'アップグレード',
  'presetDowngrade': 'ダウングレード',
  'presetItemsWillChange': '変更される項目:',
  'presetNoChange': '変更不要。現在の値が既に新しいプリセットの要件を満たしています。',
  'presetIndependentFields': '以下のパラメータはプリセットの影響を受けず、変更されません:',
  'cancel': 'キャンセル',
  'confirmSwitch': '切り替え確認',
  'applying': '適用中...',
  'saveChanges': '変更を保存',
  'saving': '保存中...',
  'configHistory': '設定履歴',
  'historyLoading': '読み込み中...',
  'noHistory': '設定履歴はまだありません',
  'unknownTime': '不明な時刻',
  'currentLabel': '（現在）',
  'rollbackTo': 'この時点にロールバック',
  'rollingBack': 'ロールバック中...',
  'currentState': '現在:',
  'banDuration': '禁止期間（時間、72以下）',
  'reasonOptional': '理由（任意）',
  'reasonPlaceholder': '簡単な説明...',
  'switchState': '切替',
  'switchingState': '切り替え中...',
  'importSoulTitle': 'AI ソウルアーカイブをインポート',
  'unnamed': '名称未設定',
  'memoryCount': 'メモリ数:',
  'knowledgeCount': 'ナレッジベース:',
  'configHistoryCount': '設定履歴:',
  'archiveVersion': 'アーカイブ版:',
  'importMemories': 'メモリをインポート',
  'importKnowledge': 'ナレッジベースをインポート（本文ごと）',
  'importedTitle': 'インポート完了',
  'importedMemories': 'メモリ:',
  'importedDetails': '本文セグメント数:',
  'importedKnowledge': 'ナレッジ文書:',
  'importedDone': '完了',
  'importButton': 'インポート',
  'importing': 'インポート中...',
  'invalidFile': '無効なファイル形式です。JSONファイルを選択してください',
  'stateLabelsActive': 'オンライン',
  'stateLabelsDnd': 'おやすみ',
  'stateLabelsOffline': 'オフライン',
  'stateLabelsBlocked': 'ブロック',
  'presetLabelChat': 'チャット向け',
  'presetLabelImmersive': '深い没入向け',
  'presetLabelDigitalLife': 'デジタル生命向け',
  'paramThinkingEnabled': '深い推論',
  'paramMaxToolRounds': '返信ラウンド上限',
  'paramAlarmMaxToolRounds': 'アラームラウンド上限',
  'paramForceAlarmOnEnd': '強制アラーム設定',
  'paramMaxAlarms': '最大アラーム数',
  'paramIsAiEditable': '自己編集可能',
  'paramHideAiIdentity': 'AIであることを隠す',
  'paramDelayReplyEnabled': '遅延返信',
  'discoverable': 'このAIを他人が検出可能にする',
  'discoverableDesc': 'オフにすると検索結果に表示されず、プロフィールクリックでのフレンド申請も受け付けません',
  'allowOthersChat': '他のユーザーがこのAIと会話することを許可',
  'dmQuotaTitle': 'AI↔AI DM 上限',
  'dmQuotaDesc': '他の AI とやり取りできる DM 件数を制限します（0 = その項目は無効）。超過後もメッセージは届きますが、相手の自動返信は発生しません。作成者に送るとカウントがリセットされます。',
  'dmQuotaSend': '送信',
  'dmQuotaDaily': '1日あたり',
  'dmQuotaWeekly': '1週間あたり',
  'dmQuotaCreatorChat': '作成者との会話以降',
  'dmQuotaReceive': '受信',
  'allowOthersChatDesc': 'オフにすると所有者と共同編集者のみがAIの返信をトリガーできます',
  'othersChatUnlimited': '常に許可（無制限）',
  'othersChatQuota': 'トリガー回数を制限',
  'othersChatQuotaLabel': 'クォータ上限（回）',
  'othersChatUsed': '使用済み',
  'othersChatUsedReset': 'リセット',
  'othersChatQuotaDesc': '回数を使い切ると「他のユーザーとの会話を許可」が自動オフになり、下の禁止モードに移行します',
  'disallowStrict': '厳格に禁止',
  'disallowOwnKey': '独自のAPI Key使用を許可',
  'disallowOwnKeyDesc': '他のユーザーが自分のAPI Keyを使ってAIと会話でき、あなたのクォータを消費しません',
  'disallowModeLabel': '禁止時の処理',
  'autoResetQuota': 'クォータ自動リセット',
  'autoResetQuotaDesc': 'DM会話ごとに使用回数を0にリセットし、ユーザーが常に最大クォータで会話できるようにします',
  'groupOwnerPays': 'グループチャットはグループ所有者が負担',
  'groupOwnerPaysDesc': 'グループ内のAIメッセージはグループ所有者が支払います。無効にするとユーザーは独自のAPI Keyを使用する必要があります',
  'quotaExhaustedNotice': 'あなたのAI「{name}」が他のユーザーによって{used}回トリガーされ、クォータ上限（{quota}）に達しました。「他のユーザーとの会話を許可」が自動的にオフになりました。',
}
